import { inject } from '@angular/core';
import { type Action, Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import {
  combineLatest,
  defaultIfEmpty,
  first,
  forkJoin,
  map,
  type Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { type CanvasElementFrom, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { ComparisonOperator, type TableInfo } from '@blaze-shared/api-contracts-domain';
import { TablesCollectionService } from '@blaze-shared/datasets-data-access';
import { SearchAndFiltersMode } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';

import { ElementsFacade, updateElementProps } from '../../../+state/elements';
import { type EngineState } from '../../../+state/engine';
import { RuntimeFacade } from '../../../+state/runtime/runtime.facade';
import {
  addWorkflowWithConnection,
  createNewWorkflowRequest,
  type ElementHandler,
  findElement,
  getElement,
  upsertElement,
} from '../../../ai';
import { WorkflowCrudService } from '../../../services/workflow-crud.service';

import {
  type DataFilterColumn,
  type DataFilterColumnOperator,
  type DataFilterContext,
  type DataFilterResolvedColumn,
  type DataFilterResolvedContext,
} from './data-filter.definition';

export class DataFilterElementHandler implements ElementHandler<'dataFilter'> {
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly workflowCrudService = inject(WorkflowCrudService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly tableService = inject(TablesCollectionService);

  readonly type = 'dataFilter';

  prepare(context: DataFilterContext): Observable<DataFilterResolvedContext> {
    const tableName = context.element.table;

    if (!tableName) {
      return of(context);
    }

    const tableId = context.datasetNameToId[tableName];

    if (!tableId) {
      return of(context);
    }

    return this.tableService.entities$.pipe(
      map((tables) => tables.find((t) => t.id === tableId)),
      first(),
      switchMap((table) => {
        if (!table) {
          return of({});
        }

        return forkJoin({
          workflow: this.workflowCrudService.createWorkflow(
            createNewWorkflowRequest(table.id, context.pageId, context.projectId),
          ),
          schema: this.schemaService.get(table.id).pipe(first(Boolean)),
        }).pipe(
          map(({ workflow }) => ({
            workflow,
            columns: context.element.columns
              ? this.resolveColumns(table, context.element.columns)
              : undefined,
          })),
        );
      }),
      map((result) => ({
        ...context,
        ...result,
      })),
    );
  }

  process(context: DataFilterResolvedContext, draft: WritableDraft<EngineState>): void {
    let props = this.getInitialProps(context, draft);

    if (context.element.search !== undefined) {
      props = applyPropsPatch(props, {
        content: {
          searchAndFilters: {
            searchEnabled: context.element.search,
            injectToElement: true,
            mode: SearchAndFiltersMode.PREDEFINED,
            filters: [],
            useUserSelectedSearchColumns: !!context.element.columns?.length,
          },
        },
      });
    }

    if (context.workflow) {
      props = applyPropsPatch(props, {
        data: [
          {
            key: 'value',
            enabled: true,
            bindings: {},
            isCustomSelection: false,
            dataSource: {
              sourceType: 'data-filter',
              id: context.workflow.id,
              columns: [],
              name: 'Employees',
            },
          },
        ],
      });
    }

    const result = upsertElement(draft, context, props);
    const element = getElement(draft, result.elementId, 'dataFilter');

    element.width = {
      mode: 'full',
    };

    if (context.workflow) {
      addWorkflowWithConnection(draft, context, context.workflow);
    }
  }

  finalize(
    element: CanvasElementFrom<'dataFilter'>,
    context: DataFilterResolvedContext,
  ): Observable<void> {
    return combineLatest({
      entities: this.elementsFacade.entities$,
      workflows: this.runtimeFacade.workflows$,
    }).pipe(
      first(),
      switchMap(({ entities, workflows }) => {
        const binding$ = this.elementsFacade.getUpdateBindingAction(
          {
            elementId: element.id,
            bindingKey: 'value',
            isNewDataSource: false,
          },
          entities,
          workflows,
        );

        return binding$.pipe(defaultIfEmpty<Action, undefined>(undefined));
      }),
      tap((action) => {
        if (action) {
          this.store.dispatch(action);
        }

        if (context.columns?.length) {
          this.store.dispatch(
            updateElementProps({
              elementId: element.id,
              path: 'content.searchAndFilters.filters',
              value: context.columns.map((column, index) => ({
                id: index,
                placeholder: column.name,
                filterField: column.id,
                operator: column.operator,
              })),
            }),
          );
        }
      }),
      map((): void => undefined),
    );
  }

  private getInitialProps(
    context: DataFilterContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'dataFilter'> {
    const element = findElement<'dataFilter'>(draft, context.element.id);
    return (
      element?.props ?? {
        featuresConfig: {
          fieldsLabelGen: 2,
        },
      }
    );
  }

  private resolveColumns(
    table: TableInfo,
    columns: DataFilterColumn[],
  ): DataFilterResolvedColumn[] {
    if (!columns?.length) {
      return [];
    }

    return columns
      .map((column) => ({
        id: table.columns.find((c) => c.name === column.name)?.id ?? '',
        name: column.name,
        operator: column.operator ? this.resolveColumnOperator(column.operator) : undefined,
      }))
      .filter((c) => Boolean(c.id));
  }

  private resolveColumnOperator(operator: DataFilterColumnOperator): ComparisonOperator {
    switch (operator) {
      case 'GreaterEqual':
        return ComparisonOperator.Gte;
      case 'Greater':
        return ComparisonOperator.Gt;
      case 'Equal':
        return ComparisonOperator.Eq;
      case 'Less':
        return ComparisonOperator.Lt;
      case 'LessEqual':
        return ComparisonOperator.Lte;
    }
  }
}
